/**
 * Átomos Jotai com escopo da feature **Configuração de Nota e Frequência**.
 *
 * Nunca usar `useAtom` diretamente nos componentes — o acesso é encapsulado pelos
 * hooks da feature (`useGradeConfig`, `useGradeConfigTabs`, `useBulkLink`).
 * Toda a persistência é apenas em memória (reseta ao recarregar).
 */

import { atom } from 'jotai';

import type { ClassGroup } from '../../types/gradeConfig';

/** Filtro de módulo aplicado à tabela de vínculos (`'all'` agrupa, `'none'` = sem módulo). */
export type FilterModule = string | 'all' | 'none';

/** Aba ativa do template. */
export type GradeTab = 'config' | 'resultado';

/** Estado principal: turmas carregadas, turma selecionada e filtros da tabela. */
export interface GradeConfigState {
  /**
   * Turma(s) em edição — de 0 a 1 item nesta entrega. É a working copy editável
   * (campos UI-only) semeada a partir da query do Kubb, não o cache de servidor.
   */
  readonly classGroups: readonly ClassGroup[];
  /** `classStudentId` da turma selecionada (`''` = nenhuma). */
  readonly selectedClassId: string;
  /** Rótulo da turma selecionada (valor do dropdown `class-students`). */
  readonly selectedClassName: string;
  readonly filterModule: FilterModule;
}

/**
 * Aula selecionada no modal de vínculo, persistida entre buscas.
 *
 * O vínculo ("Anexar Aulas ao Módulo") envia `id` (id da aula = `classes[].id`) em
 * `classroomDisciplineIds` — não o id da disciplina. Os demais campos guardam o contexto da
 * disciplina para renderizar a linha mesmo quando uma nova busca troca a listagem.
 */
export interface BulkSelectedLesson {
  /** Id da aula (`DisciplineClassItemViewModel.id`) — o que é enviado no vínculo. */
  readonly id: string;
  /** Id da disciplina à qual a aula pertence (`without-module` `id`). */
  readonly disciplineId: string;
  readonly disciplineName: string;
  /** Número sequencial da aula na disciplina (1-based). */
  readonly lessonNumber: number;
  /** Data (ISO) da aula. */
  readonly date: string;
}

/** Parâmetros da listagem (`without-module`) acionada pelo botão "Buscar". */
export interface BulkListingRequest {
  /** `disciplineId` (do `key` da disciplina escolhida no autocomplete). */
  readonly disciplineId: number;
  /** Período inicial (ISO) ou `undefined`. */
  readonly startDate?: string;
  /** Período final (ISO) ou `undefined`. */
  readonly endDate?: string;
}

/** Estado do modal de vínculo em lote. */
export interface BulkLinkState {
  readonly open: boolean;
  /** Período inicial (ISO `YYYY-MM-DD`) ou `''`. */
  readonly from: string;
  /** Período final (ISO `YYYY-MM-DD`) ou `''`. */
  readonly to: string;
  readonly search: string;
  /** Disciplina escolhida no autocomplete de busca (`shared/disciplines`). */
  readonly pickedId: string;
  readonly pickedName: string;
  /**
   * Parâmetros da última busca acionada pelo botão "Buscar" (`null` = ainda não buscou).
   * A listagem do grid (`without-module`) só é consultada quando este campo é definido.
   */
  readonly listingReq: BulkListingRequest | null;
  /**
   * Aulas selecionadas, indexadas pelo id da aula. Guarda os metadados (não só um booleano)
   * para que a seleção permaneça no grid mesmo quando uma nova busca troca os resultados.
   */
  readonly selected: Readonly<Record<string, BulkSelectedLesson>>;
  /** Módulo de destino do vínculo em lote (`''` = sem módulo). */
  readonly module: string;
}

/** Notas/presenças do simulador, indexadas pela chave da unidade de avaliação. */
export interface SimulatorState {
  readonly grades: Readonly<Record<string, string>>;
  readonly attendance: Readonly<Record<string, string>>;
}

const INITIAL_STATE: GradeConfigState = {
  classGroups: [],
  selectedClassId: '',
  selectedClassName: '',
  filterModule: 'all',
};

/** Estado principal da feature (turmas + seleção + filtros). */
export const gradeConfigStateAtom = atom<GradeConfigState>(INITIAL_STATE);

/** Aba ativa (Configuração × Resumo & simulador). */
export const gradeTabAtom = atom<GradeTab>('config');

/** Mapa de expansão das linhas de disciplina (id da atividade → expandida). */
export const gradeExpandedAtom = atom<Readonly<Record<string, boolean>>>({});

/** Estado do modal de vínculo em lote. */
export const bulkLinkAtom = atom<BulkLinkState>({
  open: false,
  from: '',
  to: '',
  search: '',
  pickedId: '',
  pickedName: '',
  listingReq: null,
  selected: {},
  module: '',
});

/** Notas/presenças simuladas. */
export const gradeSimulatorAtom = atom<SimulatorState>({ grades: {}, attendance: {} });
